package com.eladfinish.bible.contest.screens.search.usecases.specialSearch

import com.eladfinish.bible.contest.common.usecases.RemoveNikudAndTeamimAndMakafUseCase
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.extensions.splitByRegex
import com.eladfinish.bible.contest.screens.search.model.ChapterDetails
import io.github.aakira.napier.Napier
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject

/**
 * Developed by
 * @author Elad Finish
 */

class LexicographicOrder(
    private val includeLog: Boolean = false,
    private val lexicoType: LexicoType,
) : SpecialSearch,
    KoinComponent {

    private val sb = StringBuilder()

    private val lexicographic = true
    private var max = 0

    private var largestWords = mutableSetOf<String>()

    private val removeNikudAndTeamimAndMakafUseCase by inject<RemoveNikudAndTeamimAndMakafUseCase>()

    override fun process(chapterDetails: ChapterDetails) = with(chapterDetails) {
        verses.forEachIndexed { index, verse ->
            verse.trim().splitByRegex(StringConstants.REGEX_SPACE).let {
                it.forEach { word ->
                    if (lexicographic) {
                        val onlyLetters = removeNikudAndTeamimAndMakafUseCase(word)
                            .replace(COLON_SPLITTER, "")
                        val isSorted = onlyLetters.isWordSortedLexicographically(lexicoType)
                        val length = onlyLetters.length

                        if (isSorted) {
                            if (length > max) {
                                max = length
                                largestWords = mutableSetOf(onlyLetters)
                                if (includeLog) {
                                    sb.append(onlyLetters to getSource(index) + verse).append("\n")
                                }
                            } else if (length == max) {
                                largestWords.add(onlyLetters)
                                if (includeLog) {
                                    sb.append(onlyLetters to getSource(index) + verse).append("\n")
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    override fun getSummary(): String {
        return sb.apply {
            if (includeLog) {
                append("\n@@@\n")
            }
            append("מילים בסדר לקסיקוגרפי " + lexicoType.inequalityDescription() + " - הארוכות ביותר:").append("\n")
            append(largestWords).append("← $max ").append("אותיות")
            append("\n")
            Napier.d("largestWords $largestWords")
        }.toString()
    }

    private fun String.isWordSortedLexicographically(lexicoType: LexicoType): Boolean =
        this.zipWithNext { a, b -> lexicoType.inequalityType()(a.code, b.code) }.all { it }

    enum class LexicoType {     //דוגמא למילה כזו:
        INCREASING,             //בבגדים
        STRICTLY_INCREASING,    //אחימעץ
        DECREASING,             //תתמוגג
        STRICTLY_DECREASING,    //תשקצנו
        ;

        fun inequalityType(): (Int, Int) -> Boolean =
            { a: Int, b: Int ->
                when (this) {
                    INCREASING -> a <= b
                    STRICTLY_INCREASING -> a < b
                    DECREASING -> a >= b
                    STRICTLY_DECREASING -> a > b
                }
            }

        fun inequalityDescription(): String =
            when (this) {
                INCREASING -> "עולה"
                STRICTLY_INCREASING -> "עולה ממש"
                DECREASING -> "יורד"
                STRICTLY_DECREASING -> "יורד ממש"
            }
    }

}

// TODO: write tests:

/*
INCREASING
מילים בסדר לקסיקוגרפי עולה
מילים בסדר לקסיקוגרפי עולה - הארוכות ביותר:
[בחצצרת, בגדיכם, אחימעץ, בבגדים]-> 6 אותיות

STRICTLY_INCREASING
מילים בסדר לקסיקוגרפי עולה ממש
מילים בסדר לקסיקוגרפי עולה ממש - הארוכות ביותר:
[בגדיכם, אחימעץ]-> 6 אותיות

DECREASING
מילים בסדר לקסיקוגרפי יורד
מילים בסדר לקסיקוגרפי יורד - הארוכות ביותר:
[תשרפנו, תשקצנו, תצליחו, תתנודד, מממלכה, תתמוגג, תשרפנה, תצפנני]-> 6 אותיות

STRICTLY_DECREASING
מילים בסדר לקסיקוגרפי יורד ממש
מילים בסדר לקסיקוגרפי יורד ממש - הארוכות ביותר:
[תשרפנו, תשקצנו, תצליחו, תשרפנה]-> 6 אותיות

 */
